import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ReportType } from 'app/core/enums/common.enum';
import {
    catchError,
    combineLatest,
    filter,
    map,
    of,
    startWith,
    switchMap,
    zip,
} from 'rxjs';
import { ReportAdapterService } from '../services/report-adapter.service';
import { ReportAPIService } from '../services/report-api.service';
import * as ReportActions from './report.actions';
import {
    selectReportOfficeExpenses,
    selectReportOfficeExpensesLoading,
    selectReportOnsiteExpenses,
    selectReportOnsiteExpensesLoading,
    selectReportShowExpenses,
    selectReportShowExpensesLoading,
    selectReportSummary,
    selectReportSummaryLoading,
} from './report.selectors';

@Injectable()
export class ReportEffect {
    private _actions$ = inject(Actions);
    private _store = inject(Store);
    private reportAPI = inject(ReportAPIService);
    private adapter = inject(ReportAdapterService);

    isLoading1$ = this._store.select(selectReportSummaryLoading);
    isLoading2$ = this._store.select(selectReportShowExpensesLoading);
    isLoading3$ = this._store.select(selectReportOfficeExpensesLoading);
    isLoading4$ = this._store.select(selectReportOnsiteExpensesLoading);

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------
    // -----------------------------------------------------------
    // @ Report Generation Effects
    // -----------------------------------------------------------

    // Generate Report
    generateReport$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ReportActions.generateReport),

            map((action) => action.reportType),
            switchMap((reportType: ReportType) => {
                // console.log('Report Type: ', reportType);
                this.dispatchReportActions(reportType);

                return combineLatest([
                    this.isLoading1$,
                    this.isLoading2$,
                    this.isLoading3$,
                    this.isLoading4$,
                ]);
            }),
            switchMap(() => {
                return zip([
                    this._store
                        .select(selectReportSummary)
                        .pipe(startWith(null)),
                    this._store
                        .select(selectReportShowExpenses)
                        .pipe(startWith(null)),
                    this._store
                        .select(selectReportOfficeExpenses)
                        .pipe(startWith(null)),
                    this._store
                        .select(selectReportOnsiteExpenses)
                        .pipe(startWith(null)),
                ]);
            }),
            map((reportArr) => ({
                summary: reportArr[0],
                showExpenses: reportArr[1],
                officeExpenses: reportArr[2],
                onsiteExpenses: reportArr[3],
            })),
            filter(
                (reportObj) =>
                    reportObj.summary !== null &&
                    reportObj.showExpenses !== null &&
                    reportObj.officeExpenses !== null &&
                    reportObj.onsiteExpenses !== null
            ),
            // Debug
            // tap((res) => {
            //     console.log('Report', res);
            // }),
            map((report) => ReportActions.generateReportSuccess({ report })),
            catchError((error) =>
                of(
                    ReportActions.generateReportFailure({
                        error: error.error?.message || 'Unknown error',
                    })
                )
            )
        )
    );

    private dispatchReportActions(reportType: ReportType): void {
        if (reportType === ReportType.PLAYDATES) {
            this._store.dispatch(ReportActions.getReportSummaryPlaydates());
            this._store.dispatch(
                ReportActions.getReportShowExpensesPlaydates()
            );
            this._store.dispatch(
                ReportActions.getReportOfficeExpensesPlaydates()
            );
            this._store.dispatch(
                ReportActions.getReportOnsiteExpensesPlaydates()
            );
        }

        if (reportType === ReportType.DATE_RANGE) {
            this._store.dispatch(ReportActions.getReportSummaryDateRange());
            this._store.dispatch(
                ReportActions.getReportShowExpensesDateRange()
            );
            this._store.dispatch(
                ReportActions.getReportOfficeExpensesDateRange()
            );
            this._store.dispatch(
                ReportActions.getReportOnsiteExpensesDateRange()
            );
        }
    }

    // -----------------------------------------------------------
    // @ Get Report Part Effects
    // -----------------------------------------------------------

    // 1a. Get Report Summary Playdates
    getReportSummaryPlaydates$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ReportActions.getReportSummaryPlaydates),
            switchMap(() =>
                this.reportAPI.getReportSummaryPlaydates().pipe(
                    // Debug
                    // tap((res) => {
                    //     console.log('1A. getReport: SummaryPlaydates', res);
                    // }),
                    map((res) => this.adapter.adaptSummary(res.data)),
                    map((reportSummary) =>
                        ReportActions.getReportSummaryPlaydatesSuccess({
                            reportSummary,
                        })
                    ),
                    catchError((error) =>
                        of(
                            ReportActions.getReportSummaryPlaydatesFailure({
                                error: error.message,
                            })
                        )
                    )
                )
            )
        )
    );

    // 1b. Get Report Summary Date Range
    getReportSummaryDateRange$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ReportActions.getReportSummaryDateRange),
            switchMap(() =>
                this.reportAPI.getReportSummaryDateRange().pipe(
                    // Debug
                    // tap((res) => {
                    //     console.log('1B. getReport: SummaryDateRange', res);
                    // }),
                    map((res) => this.adapter.adaptSummary(res.data)),
                    map((reportSummary) =>
                        ReportActions.getReportSummaryDateRangeSuccess({
                            reportSummary,
                        })
                    ),
                    catchError((error) =>
                        of(
                            ReportActions.getReportSummaryDateRangeFailure({
                                error: error.message,
                            })
                        )
                    )
                )
            )
        )
    );

    // 2a. Get Report Show Expenses Playdates
    getReportShowExpensesPlaydates$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ReportActions.getReportShowExpensesPlaydates),
            switchMap(() =>
                this.reportAPI.getReportShowExpensesPlaydates().pipe(
                    // Debug
                    // tap((res) => {
                    //     console.log(
                    //         '2A. getReport: ShowExpensesPlaydates',
                    //         res
                    //     );
                    // }),
                    map((res) => this.adapter.adaptShowExpenses(res.data)),
                    map((reportShowExpenses) =>
                        ReportActions.getReportShowExpensesPlaydatesSuccess({
                            reportShowExpenses,
                        })
                    ),
                    catchError((error) =>
                        of(
                            ReportActions.getReportShowExpensesPlaydatesFailure(
                                {
                                    error: error.message,
                                }
                            )
                        )
                    )
                )
            )
        )
    );

    // 2b. Get Report Show Expenses Date Range
    getReportShowExpensesDateRange$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ReportActions.getReportShowExpensesDateRange),
            switchMap(() =>
                this.reportAPI.getReportShowExpensesDateRange().pipe(
                    // Debug
                    // tap((res) => {
                    //     console.log(
                    //         '2B. getReport: ShowExpensesDateRange',
                    //         res
                    //     );
                    // }),
                    map((res) => this.adapter.adaptShowExpenses(res.data)),
                    map((reportShowExpenses) =>
                        ReportActions.getReportShowExpensesDateRangeSuccess({
                            reportShowExpenses,
                        })
                    ),
                    catchError((error) =>
                        of(
                            ReportActions.getReportShowExpensesDateRangeFailure(
                                {
                                    error: error.message,
                                }
                            )
                        )
                    )
                )
            )
        )
    );

    // 3a. Get Report Office Expenses Playdates
    getReportOfficeExpensesPlaydates$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ReportActions.getReportOfficeExpensesPlaydates),
            switchMap(() =>
                this.reportAPI.getReportOfficeExpensesPlaydates().pipe(
                    // Debug
                    // tap((res) => {
                    //     console.log(
                    //         '3A. getReport: OfficeExpensesPlaydates',
                    //         res
                    //     );
                    // }),
                    map((res) => this.adapter.adaptOfficeExpenses(res.data)),
                    map((reportOfficeExpenses) =>
                        ReportActions.getReportOfficeExpensesPlaydatesSuccess({
                            reportOfficeExpenses,
                        })
                    ),
                    catchError((error) =>
                        of(
                            ReportActions.getReportOfficeExpensesPlaydatesFailure(
                                {
                                    error: error.message,
                                }
                            )
                        )
                    )
                )
            )
        )
    );

    // 3b. Get Report Office Expenses Date Range
    getReportOfficeExpensesDateRange$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ReportActions.getReportOfficeExpensesDateRange),
            switchMap(() =>
                this.reportAPI.getReportOfficeExpensesDateRange().pipe(
                    // Debug
                    // tap((res) => {
                    //     console.log(
                    //         '3B. getReport: OfficeExpensesDateRange',
                    //         res
                    //     );
                    // }),
                    map((res) => this.adapter.adaptOfficeExpenses(res.data)),
                    map((reportOfficeExpenses) =>
                        ReportActions.getReportOfficeExpensesDateRangeSuccess({
                            reportOfficeExpenses,
                        })
                    ),
                    catchError((error) =>
                        of(
                            ReportActions.getReportOfficeExpensesDateRangeFailure(
                                {
                                    error: error.message,
                                }
                            )
                        )
                    )
                )
            )
        )
    );

    // 4a. Get Report Onsite Expenses Playdates
    getReportOnsiteExpensesPlaydates$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ReportActions.getReportOnsiteExpensesPlaydates),
            switchMap(() =>
                this.reportAPI.getReportOnsiteExpensesPlaydates().pipe(
                    // Debug
                    // tap((res) => {
                    //     console.log(
                    //         '4A. getReport: OnsiteExpensesPlaydates',
                    //         res
                    //     );
                    // }),
                    map((res) => this.adapter.adaptOnsiteExpenses(res.data)),
                    map((reportOnsiteExpenses) =>
                        ReportActions.getReportOnsiteExpensesPlaydatesSuccess({
                            reportOnsiteExpenses,
                        })
                    ),
                    catchError((error) =>
                        of(
                            ReportActions.getReportOnsiteExpensesPlaydatesFailure(
                                { error: error.message }
                            )
                        )
                    )
                )
            )
        )
    );

    // 4b. Get Report Onsite Expenses Date Range
    getReportOnsiteExpensesDateRange$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ReportActions.getReportOnsiteExpensesDateRange),
            switchMap(() =>
                this.reportAPI.getReportOnsiteExpensesDateRange().pipe(
                    // Debug
                    // tap((res) => {
                    //     console.log(
                    //         '4B. getReport: OnsiteExpensesDateRange',
                    //         res
                    //     );
                    // }),
                    map((res) => this.adapter.adaptOnsiteExpenses(res.data)),
                    map((reportOnsiteExpenses) =>
                        ReportActions.getReportOnsiteExpensesDateRangeSuccess({
                            reportOnsiteExpenses,
                        })
                    ),
                    catchError((error) =>
                        of(
                            ReportActions.getReportOnsiteExpensesDateRangeFailure(
                                { error: error.message }
                            )
                        )
                    )
                )
            )
        )
    );
}
